/**
 * `useSignEntity` Hook
 *
 * The `useSignEntity` hook facilitates signing processes using multiple methods,
 * such as BankID and Phone OTP (One-Time Password). It integrates the functionalities
 * of `useBankId` and `usePhoneOtp` hooks, providing a unified interface for managing
 * signing and cancellation operations.
 *
 * @template TPayload - The type of the payload used in the signing function.
 * @template TError - The type of error expected from the signing function, extending `SigningErrorResponse`.
 *
 * @param {Object} params - The parameters object.
 * @param {UseMutateFunction<AxiosResponse<any, any>, AxiosError<TError>, TPayload, unknown>} params.signFn
 *        A function that handles the signing operation, typically used to trigger
 *        the signing process using a backend service.
 * @param {UseMutateFunction<AxiosResponse<any, any>, AxiosError<unknown>, any, unknown>} params.cancelFn
 *        A function that handles the cancellation of a signing operation, allowing
 *        the user or system to abort an ongoing signing process.
 *
 * @returns {Object} An object containing two properties:
 * - `bankId`: An object returned from the `useBankId` hook, managing the BankID signing method,
 *             providing methods and states related to the BankID signing process.
 * - `phoneOtp`: An object returned from the `usePhoneOtp` hook, managing the Phone OTP signing method,
 *               providing methods and states for handling OTP-based signing.
 *
 * **/

import { SigningErrorResponse } from '@/types/sign';
import { UseMutateFunction } from '@tanstack/react-query';
import { AxiosError, AxiosResponse } from 'axios';
import { useBankId } from './useBankId';
import { usePhoneOtp } from './usePhoneOtp';

export const useSignEntity = <TPayload, TError extends SigningErrorResponse>({
  cancelFn,
  signFn,
}: {
  signFn: UseMutateFunction<
    AxiosResponse,
    AxiosError<TError>,
    TPayload,
    unknown
  >;
  // biome-ignore lint/suspicious/noExplicitAny:
  cancelFn: UseMutateFunction<AxiosResponse, AxiosError<unknown>, any, unknown>;
}) => {
  const bankId = useBankId<TPayload, TError>({
    signFn,
    cancelFn,
  });
  const phoneOtp = usePhoneOtp<TPayload, TError>({
    signFn,
    cancelFn,
  });

  return {
    bankId,
    phoneOtp,
  };
};
